import React, { useMemo, useRef } from 'react';
import {
  ShareInline,
  ShareModal,
  type ShareEventProps,
  ShareFlyerProps,
  ShareFlyer,
} from '@eventbrite/legacy.social-share';
import { useIntl } from 'react-intl';
import { GenericLazyString } from '@eventbrite/legacy-infra.i18n';
import { useAppContext } from '@shared/Wrapper';
import { useIsMobile } from '@components/Agenda/_shared/useIsMobile';
import { AffiliateCodes, GACodes } from '@/components/_shared/constants';
import styles from './ShareButton.module.scss';
import { useBasicInfo } from '@shared/contexts/BasicInfoContext';
import { useGallery } from '@shared/contexts/GalleryContext';
import { getImageProps } from 'next/image';
import { BasicInfo } from '@/types';
import { useFeatureFlags } from '@shared/contexts';

const LISTING_UTM_OPTIONS = {
  'utm-campaign': 'social',
  'utm-content': 'attendeeshare',
  'utm-medium': 'discovery',
  'utm-term': 'listing',
};

const TWITTER_HANDLE = 'eventbritehelp';

export interface ShareButtonProps {
  eventId: string;
  eventName: string;
  eventUrl: string;
  isShareable?: boolean;
}

const formatUrlWithUtm = (
  url: string,
  utmOptions: Record<string, string>,
  additionalParams?: Record<string, string>,
): string => {
  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    // Add UTM parameters
    Object.entries(utmOptions).forEach(([key, value]) => {
      params.set(key, value);
    });

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.set(key, value);
      });
    }

    urlObj.search = params.toString();
    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original URL
    return url;
  }
};

export const ShareButton: React.FC<ShareButtonProps> = ({ eventId, eventName, eventUrl, isShareable = false }) => {
  const {
    data: { serverUrl, locale },
  } = useAppContext();
  const isMobile = useIsMobile();
  const { formatMessage } = useIntl();
  const modalContainer = useRef<HTMLDivElement>(null);

  if (!isShareable) {
    return null;
  }

  const facebookOptions = {
    appId: '28218816837',
    locale: locale.replace('-', '_'),
    page: 'www.facebook.com/Eventbrite',
    version: 'v12.0',
    xfbml: true,
  };

  const shareEventProps: ShareEventProps = {
    eventId,
    eventName,
    eventUrl,
    facebookOptions,
    serverUrl,
    twitterHandle: TWITTER_HANDLE,
    isMobile,
    trackingCategory: GACodes.GA_CATEGORY_LISTING,
    utmOptions: LISTING_UTM_OPTIONS,
  };

  const shareUrl = formatUrlWithUtm(eventUrl, LISTING_UTM_OPTIONS, {
    'utm-source': 'cp',
    aff: AffiliateCodes.AFF_SHARE_COPY_URL,
  });

  const tooltipTitle = new GenericLazyString(() =>
    formatMessage({ id: 'Share this event', defaultMessage: 'Share this event' }),
  );
  const tooltipText = new GenericLazyString(() => formatMessage({ id: 'Share Event', defaultMessage: 'Share Event' }));

  return (
    <div data-testid="share-button-container">
      <div ref={modalContainer} id="share-modal-wrapper" />
      <div className={styles.tooltipStackingContext}>
        <ShareModal
          title={formatMessage({ id: 'Share with friends', defaultMessage: 'Share with friends' })}
          inputLabel={formatMessage({ id: 'Event URL', defaultMessage: 'Event URL' })}
          url={shareUrl}
          tooltipTitle={tooltipTitle}
          tooltipText={tooltipText}
          trackingCategory={GACodes.GA_CATEGORY_LISTING}
          isMobile={isMobile}
          container={modalContainer}
          eventId={eventId}
        >
          {!isMobile ? <ShareInline {...shareEventProps} /> : null}
          <ShareFlyerWrapper />
        </ShareModal>
      </div>
    </div>
  );
};

export const ShareFlyerWrapper = () => {
  const { formatMessage, locale } = useIntl();
  const basicInfo = useBasicInfo();
  const gallery = useGallery();
  const featureFlags = useFeatureFlags();
  const image = gallery?.images?.[0]?.url;
  const isMobile = useIsMobile();
  const shareFlyerProps = useMemo(
    () => getShareFlyerProps(basicInfo, image, formatMessage, locale, featureFlags.shareFlyer, isMobile),
    [basicInfo, image, formatMessage, locale, featureFlags.shareFlyer, isMobile],
  );
  return shareFlyerProps ? <ShareFlyer {...shareFlyerProps} /> : null;
};

const getShareFlyerProps = (
  basicInfo: BasicInfo,
  image: string | undefined,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
  locale: string,
  featureFlagEnabled: boolean,
  isMobile: boolean,
): ShareFlyerProps | undefined => {
  if (!featureFlagEnabled) {
    return undefined;
  }
  let src = image;
  if (src) {
    src = getImageProps({
      alt: '',
      src,
      width: 320,
      height: 160,
    }).props.src;
  }

  return {
    isMobile,
    event: {
      title: basicInfo.name,
      imageSrc: src,
      organizerName: basicInfo.organizer.name ?? '',
      date: {
        utc: basicInfo.startDate.utc!,
        timeZone: basicInfo.startDate.timezone!,
      },
      locale,
      isOnline: basicInfo.isOnline,
      venue: basicInfo.venue?.name,
      city: basicInfo.venue?.address?.city,
      id: parseInt(basicInfo.id),
      host: new URL(basicInfo.url).host,
    },
    texts: {
      byPrefix: formatMessage({ id: 'By', defaultMessage: 'By' }),
      attendOn: formatMessage({ id: 'Attend on', defaultMessage: 'Attend on' }),
      widgetTitle: formatMessage({ id: 'Share a flyer', defaultMessage: 'Share a flyer' }),
      save: formatMessage({ id: 'Save', defaultMessage: 'Save' }),
      saving: formatMessage({ id: 'Saving...', defaultMessage: 'Saving...' }),
      onlineVenue: formatMessage({ id: 'Online', defaultMessage: 'Online' }),
      share: formatMessage({ id: 'Share', defaultMessage: 'Share' }),
    },
    shareData: {
      text: basicInfo.name,
      title: basicInfo.name,
      url: formatUrlWithUtm(basicInfo.url, LISTING_UTM_OPTIONS, {
        'utm-source': 'wsa',
        aff: AffiliateCodes.AFF_SHARE_WEB_MOBILE,
      }),
    },
  };
};
